import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { MapPin, ChevronDown, Search, X, Loader2 } from 'lucide-react';
import apiClient from '@/utils/api';
import { getConfig } from '@/utils/runtimeConfig';

// ---------- Google Places (NEW API) script loader (shared with LocationSearch) ----------
let _scriptPromise = null;

function loadGoogleScript() {
  if (_scriptPromise) return _scriptPromise;
  if (window.google?.maps?.importLibrary) {
    _scriptPromise = Promise.resolve();
    return _scriptPromise;
  }
  const { google_places_key } = getConfig();
  if (!google_places_key) return Promise.reject(new Error('No Google Places key'));

  _scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-places-script');
    if (existing) { existing.addEventListener('load', resolve); return; }
    const s = document.createElement('script');
    s.id = 'google-places-script';
    s.src = `https://maps.googleapis.com/maps/api/js?key=${google_places_key}&v=weekly&loading=async&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// ---------- Lightweight frontend cache ----------
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;

function cGet(key) {
  const e = _cache.get(key);
  if (e && Date.now() - e.ts < CACHE_TTL) return e.data;
  _cache.delete(key);
  return null;
}
function cSet(key, data) {
  _cache.set(key, { data, ts: Date.now() });
  if (_cache.size > 300) _cache.delete(_cache.keys().next().value);
}

// ---------- Component ----------
const CitySelector = ({
  selectedCity,
  onCityChange,
  variant = 'default',
  className = '',
  onInteractionStart = () => {},
  onInteractionEnd = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Data states
  const [popularCities, setPopularCities] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [popularLoaded, setPopularLoaded] = useState(false);

  // Google state
  const [googleReady, setGoogleReady] = useState(false);
  const placesLib = useRef(null);
  const sessionToken = useRef(null);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Notify parent of interaction
  useEffect(() => {
    if (isOpen) onInteractionStart();
    else onInteractionEnd();
  }, [isOpen, onInteractionStart, onInteractionEnd]);

  // Fetch popular cities once (non-blocking)
  useEffect(() => {
    const fetchPopular = async () => {
      const cached = cGet('popular');
      if (cached) { setPopularCities(cached); setPopularLoaded(true); return; }
      try {
        const { data } = await apiClient.get('/locations/popular?limit=50');
        const cities = (data || []).map(c => ({ ...c, _source: 'db' }));
        cSet('popular', cities);
        setPopularCities(cities);
      } catch { /* silent */ }
      setPopularLoaded(true);
    };
    fetchPopular();
  }, []);

  // Init Google Places (new API)
  useEffect(() => {
    let cancelled = false;
    loadGoogleScript()
      .then(async () => {
        const lib = await window.google.maps.importLibrary('places');
        if (cancelled) return;
        placesLib.current = lib;
        sessionToken.current = new lib.AutocompleteSessionToken();
        setGoogleReady(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // --- DB search ---
  const fetchDB = useCallback(async (query) => {
    const key = `db:${query.toLowerCase()}`;
    const cached = cGet(key);
    if (cached) return cached;
    const { data } = await apiClient.get(`/locations/autocomplete?q=${encodeURIComponent(query)}&limit=15`);
    const results = (data || []).map(loc => ({ ...loc, _source: 'db' }));
    cSet(key, results);
    return results;
  }, []);

  // --- Google search (new Places API) ---
  const fetchGoogle = useCallback(async (query) => {
    if (!placesLib.current) return [];
    const key = `google:${query.toLowerCase()}`;
    const cached = cGet(key);
    if (cached) return cached;

    try {
      const { suggestions } = await placesLib.current.AutocompleteSuggestion
        .fetchAutocompleteSuggestions({
          input: query,
          sessionToken: sessionToken.current,
          includedRegionCodes: ['in'],
          // `(regions)` collection includes localities, sublocalities, villages,
          // postal codes and admin areas — so smaller towns/villages appear too.
          includedPrimaryTypes: ['(regions)'],
        });
      const mapped = (suggestions || [])
        .filter((s) => s.placePrediction)
        .map((s) => {
          const p = s.placePrediction;
          const mainText = p.mainText?.text || p.text?.text || '';
          const secondaryText = p.secondaryText?.text || '';
          return {
            name: mainText || (p.text?.text || '').split(',')[0],
            state: secondaryText.split(',').pop()?.trim() || '',
            display_name: p.text?.text || [mainText, secondaryText].filter(Boolean).join(', '),
            _source: 'google',
            _placeId: p.placeId,
          };
        });
      cSet(key, mapped);
      return mapped;
    } catch {
      return [];
    }
  }, []);

  // --- Hybrid search ---
  const hybridSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      let results = [];
      if (query.length < 4 || !googleReady) {
        try { results = await fetchDB(query); } catch {}
        if ((!results || results.length === 0) && googleReady) {
          results = await fetchGoogle(query);
        }
      } else {
        results = await fetchGoogle(query);
        if (!results || results.length === 0) {
          try { results = await fetchDB(query); } catch {}
        }
      }
      // Deduplicate by name (case-insensitive)
      const seen = new Set();
      const deduped = (results || []).filter(r => {
        const key = r.name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setSearchResults(deduped);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [googleReady, fetchDB, fetchGoogle]);

  // Debounced search on input change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchTerm && searchTerm.length >= 2) {
      debounceRef.current = setTimeout(() => hybridSearch(searchTerm), 300);
    } else {
      setSearchResults([]);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm, hybridSearch]);

  // The list to render: search results when searching, else popular
  const displayList = useMemo(() => {
    if (searchTerm && searchTerm.length >= 2) return searchResults;
    return popularCities;
  }, [searchTerm, searchResults, popularCities]);

  // Group by tier (popular) or source (search)
  const groupedCities = useMemo(() => {
    if (searchTerm && searchTerm.length >= 2) {
      // Searching — group by source
      const groups = {};
      for (const c of displayList) {
        const label = c._source === 'google' ? 'Google Results' : 'Database Results';
        if (!groups[label]) groups[label] = [];
        groups[label].push(c);
      }
      return groups;
    }
    // Popular — group by tier
    const groups = {};
    for (const c of displayList) {
      const label = c.tier === 1 ? 'Metro Cities' : c.tier === 2 ? 'Major Cities' : 'Other Cities';
      if (!groups[label]) groups[label] = [];
      groups[label].push(c);
    }
    return groups;
  }, [displayList, searchTerm]);

  // Click outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') { setIsOpen(true); e.preventDefault(); }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(p => Math.min(p + 1, displayList.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(p => Math.max(p - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && displayList[focusedIndex]) handleCitySelect(displayList[focusedIndex].name);
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      default: break;
    }
  };

  const handleCitySelect = (cityName) => {
    onCityChange(cityName);
    setIsOpen(false);
    setSearchTerm('');
    setSearchResults([]);
    setFocusedIndex(-1);
    // Renew Google session token after selection
    if (googleReady) {
      sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  };

  const variantStyles = {
    default: 'bg-white border border-gray-200 hover:border-primary/50 shadow-sm',
    hero: 'bg-white/95 backdrop-blur-md border-2 border-white/60 shadow-xl hover:shadow-2xl',
    minimal: 'bg-transparent border-b border-gray-300 hover:border-primary',
  };

  const quickSelectCities = ['Hyderabad', 'Bangalore', 'Mumbai', 'Delhi', 'Chennai', 'Pune'];

  return (
    <div ref={dropdownRef} className={`relative inline-block ${className}`} data-testid="city-selector">
      {/* Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={`flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-200 ${variantStyles[variant]} ${isOpen ? 'ring-2 ring-primary/30' : ''}`}
        data-testid="city-selector-trigger"
      >
        <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
        <span className="font-medium text-gray-900">{selectedCity}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="fixed z-[9999] w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-top-2 duration-200"
          style={{
            top: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().bottom + 8 : 0,
            left: dropdownRef.current ? Math.max(16, dropdownRef.current.getBoundingClientRect().left - 100) : 0,
          }}
        >
          {/* Search bar */}
          <div className="p-4 border-b bg-gray-50">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setFocusedIndex(-1); }}
                onKeyDown={handleKeyDown}
                placeholder="Type city or town name..."
                className="w-full pl-12 pr-10 py-3 text-base border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                autoFocus
                data-testid="city-selector-search"
              />
              {isSearching ? (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary animate-spin" />
              ) : searchTerm && (
                <button
                  onClick={() => { setSearchTerm(''); setSearchResults([]); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2 px-1">
              {searchTerm && searchTerm.length >= 2
                ? (isSearching ? 'Searching...' : `${displayList.length} results`)
                : `${popularCities.length} popular cities \u00B7 Type to search all India`}
            </p>
          </div>

          {/* City list */}
          <div className="max-h-80 overflow-y-auto" data-testid="city-selector-list">
            {displayList.length === 0 && !isSearching ? (
              <div className="p-6 text-center text-gray-500">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>{searchTerm ? 'No cities found' : 'Loading cities...'}</p>
              </div>
            ) : (
              Object.entries(groupedCities).map(([label, cities]) => (
                <div key={label}>
                  <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0 flex items-center justify-between">
                    <span>{label}</span>
                    <span className="text-gray-400">{cities.length}</span>
                  </div>
                  {cities.map((city) => {
                    const globalIdx = displayList.findIndex(c => c.name === city.name && c.state === city.state);
                    const locationInfo = city.district && city.district !== city.name
                      ? `${city.district}, ${city.state || ''}`
                      : city.state || '';
                    return (
                      <button
                        key={`${city.name}-${city.state}-${city._placeId || ''}`}
                        onClick={() => handleCitySelect(city.name)}
                        className={`w-full flex items-center justify-between px-4 py-3 hover:bg-primary/5 transition-colors ${
                          selectedCity === city.name ? 'bg-primary/10' : ''
                        } ${focusedIndex === globalIdx ? 'bg-primary/5' : ''}`}
                        data-testid={`city-option-${city.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center gap-3">
                          <MapPin className={`w-4 h-4 flex-shrink-0 ${selectedCity === city.name ? 'text-primary' : 'text-gray-400'}`} />
                          <div className="text-left">
                            <span className={`font-medium ${selectedCity === city.name ? 'text-primary' : 'text-gray-900'}`}>
                              {city.name}
                            </span>
                            <span className="text-sm text-gray-500 ml-2">{locationInfo}</span>
                          </div>
                        </div>
                        {selectedCity === city.name && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Quick select (only when not searching) */}
          {!searchTerm && (
            <div className="p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500 mb-3 font-medium">Popular Cities</p>
              <div className="flex flex-wrap gap-2">
                {quickSelectCities.map(city => (
                  <button
                    key={city}
                    onClick={() => handleCitySelect(city)}
                    className={`px-4 py-2 text-sm rounded-full border-2 transition-all ${
                      selectedCity === city
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                    }`}
                    data-testid={`quick-city-${city.toLowerCase()}`}
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Attribution */}
          {searchTerm && searchResults.some(s => s._source === 'google') && (
            <div className="px-4 py-2 text-[10px] text-gray-400 bg-gray-50 border-t flex justify-end items-center gap-1">
              Powered by
              <img src="https://developers.google.com/static/maps/documentation/images/google_on_white.png" alt="Google" className="h-3" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CitySelector;
